import { arrayToMap } from '@sparted/shared-library';
import { LanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import MModal from 'Components/modal/MModal/MModal';
import { MTranslateModal } from 'Components/modal/MTranslateModal/MTranslateModal';
import SActionBar, { OptionType, SActionBarProps } from 'Components/structural/SActionBar/SActionBar';
import SCallout from 'Components/structural/SCallout/SCallout';
import { AppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { redirect } from 'Libs/routing/redirect';
import { useManualCampaignActivationConfirmation } from 'Pages/Activity/Campaign/components/ActionBar/hooks/useManualCampaignActivationConfirmation';
import { useRandomCampaignActivationConfirmation } from 'Pages/Activity/Campaign/components/ActionBar/hooks/useRandomCampaignActivationConfirmation';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import useArchiveAction from 'Pages/Activity/hooks/useArchiveAction';
import {
  getCurrentBOLanguageOrDefault,
  getLanguagesAvailableWithCode,
  Language,
} from 'Services/language/languageService';
import { getLanguageSegmentationGroupItemsFromApp } from 'Services/segmentation/segmentationService';
import { duplicateCampaign } from 'Store/entities/activity/activity.api';
import { requestCampaignContentsTranslation } from 'Store/entities/activity/activity.thunks';
import { type Campaign } from 'Store/entities/activity/activity.type';

import { useCheckRequiredFields } from '../../CheckRequiredFields.hook';
import { redirectToActivityList } from '../../routing.utils';
import { CampaignFormValues } from '../../saveCampaign.hook';
import LanguagePanel from '../LanguagePanel/LanguagePanel';
import styles from './ActionBar.style';

const supportUrlFR =
  'https://support.sparted.com/hc/en-us/articles/9215381684636-Faire-une-campagne-en-mode-al%C3%A9atoire';
const supportUrlEN = 'https://support.sparted.com/hc/en-us/articles/9218072883356';

type ContentTranslationStatus = {
  total: number;
  toTranslate: number;
  toValidate: number;
};

const getActionBarStatus = (
  campaignStatus: Campaign['status'],
  contentStatus: ContentTranslationStatus,
): {
  status: SActionBarProps['status'];
  text: SActionBarProps['statusWording'];
  tooltip?: SActionBarProps['tooltipText'];
} => {
  if (contentStatus.toTranslate || contentStatus.toValidate) {
    if (contentStatus.toValidate && !contentStatus.toTranslate) {
      return {
        status: 'important',
        text: t('activities:status.validating'),
        tooltip: t('activities:status.translations_and_validations_pending_tooltip'),
      };
    }

    if (contentStatus.toTranslate) {
      return {
        status: 'important',
        text: t('activities:status.translations_pending', {
          total: contentStatus.total,
          translated: contentStatus.total - contentStatus.toTranslate,
        }),
        tooltip: t('activities:status.translations_pending_tooltip'),
      };
    }

    return {
      status: 'important',
      text: t('activities:status.validations_pending', {
        total: contentStatus.total,
        validated: contentStatus.total - contentStatus.toValidate,
      }),
      tooltip: t('activities:status.validations_pending_tooltip'),
    };
  }

  switch (campaignStatus) {
    case 'draft':
    case 'inactive':
      return { status: 'inactive', text: t('activities:status.inactive') };
    case 'live':
      return { status: 'active', text: t('activities:status.active') };
    case 'finished':
      return { status: 'active', text: t('activities:status.ended') };
    case 'upcoming':
      return { status: 'warning', text: t('activities:status.upcoming') };
    case 'archived':
      return { status: 'disabled', text: t('activities:status.archived') };
    default:
      return { status: 'disabled', text: 'UNHANDLED STATUS CASE' };
  }
};

const getLanguageOptions = (
  campaignId: number,
  languages: LanguageCode[],
  selectedLanguageCode: LanguageCode,
  completedLanguages: LanguageCode[],
  onLanguageChange: (language: LanguageCode) => void,
) => {
  const availableLanguages = getLanguagesAvailableWithCode();
  const availableLanguagesByCode = arrayToMap(availableLanguages, {
    selectKey: (lang) => lang.languageCode,
  });

  return languages.map((languageCode) => {
    const language = availableLanguagesByCode[languageCode];

    return {
      type: 'language',
      id: language.id,
      key: `${campaignId}-${language.id}`,
      props: {
        defaultLabel: t('content_translation:language_menu.default'),
        switchToLabel: t('content_translation:language_menu.switch_to'),
        validLabel: t('content_translation:language_menu.ready'),
        text: language.languageName,
        countryCode: language.countryCode,
        selected: selectedLanguageCode === languageCode,
        onClick: () => onLanguageChange(languageCode),
        status: completedLanguages.includes(languageCode) ? 'valid' : 'warning',
        statusText: completedLanguages.includes(languageCode)
          ? t('campaign:language_menu.ready')
          : t('campaign:language_menu.not_ready'),
      },
    } as const;
  });
};

export type ActionBarProps = {
  tabs: string[];
  locked: boolean;
  currentTabIndex: number;
  campaignId: number;
  campaignName: string;
  campaignStatus: Campaign['status'];
  campaignContentStatus: ContentTranslationStatus;
  campaignLastSave?: string;
  campaignLanguages: LanguageCode[];
  selectedLanguage: LanguageCode;
  disableSave?: boolean;
  saving?: boolean;
  isFormDirty: boolean;
  onLockChange: (newLockedState: boolean) => void;
  onTabChange: (index: number) => void;
  onLanguageChange: (language: LanguageCode) => void;
  onSave: () => void;
  onLive: () => void;
};

const ALERT_DURATION = 5000;

const generateErrorMessage = (missingFields: { [key in LanguageCode]?: string[] }) => {
  const allMissingFields = new Set(Object.values(missingFields).flatMap((fields) => fields));

  return (
    <>
      <span>{t('campaign:edition.update_fail_prefix', { count: allMissingFields.size })}</span>
      <ul style={styles.errorMessageList}>
        {Array.from(allMissingFields).map((field) => (
          <li key={field}>{t(`campaign:edition.${field}`)}</li>
        ))}
      </ul>
    </>
  );
};

export const ActionBar: React.FC<ActionBarProps> = ({
  tabs,
  locked,
  currentTabIndex,
  campaignId,
  campaignName,
  campaignStatus,
  campaignContentStatus,
  campaignLastSave,
  campaignLanguages,
  selectedLanguage,
  disableSave,
  saving,
  isFormDirty,
  onLockChange,
  onTabChange,
  onLanguageChange,
  onSave,
  onLive,
}) => {
  const [isLanguagePanelVisible, setLanguagePanelVisible] = useState(false);
  const [displayUnsavedWarning, setDisplayUnsavedWarning] = useState(false);
  const [visibleTranslationModal, displayTranslationModal] = useState(false);
  const [warningModalContext, setWarningModalContext] = useState<keyof typeof actionMapper>('noop');

  const { handleArchiveClick, archiveWarningModal } = useArchiveAction();

  const isArchived = campaignStatus === 'archived';
  const hasPendingTranslations = campaignContentStatus.toTranslate > 0;

  const dispatch = useDispatch<AppDispatch>();

  const { campaign } = useTypedSelector((state) => state.entities.activity.edition);

  if (!campaign) return null;

  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext<CampaignFormValues>();

  const isContentSelectionLocked = watch('mode') === 'random' && campaignStatus === 'live';

  const handleDuplicate = useCallback(async () => {
    const { languageCode } = getCurrentBOLanguageOrDefault();

    try {
      const duplicatedCampaign = await duplicateCampaign(campaignId, languageCode);
      redirect(`/activity/campaign/${duplicatedCampaign.id}`);
      enqueueBasicAlert({
        id: 'campaign_duplication_failed',
        status: 'success',
        title: t('activities:alerts.success.duplicate'),
      });
    } catch (err) {
      enqueueBasicAlert(
        {
          id: 'campaign_duplication_failed',
          status: 'error',
          title: t('activities:alerts.errors.duplicate_error_message'),
        },
        ALERT_DURATION,
      );
    }
  }, []);
  const handleExport = () => {};

  const openTranslationModal = useCallback(() => displayTranslationModal(true), [displayTranslationModal]);

  const { missingFields, completedLanguages } = useCheckRequiredFields({
    watch,
    campaignLanguages,
  });

  const actionMapper = useMemo(
    () => ({
      noop: () => {},
      back: redirectToActivityList,
      duplicate: handleDuplicate,
      translate: openTranslationModal,
    }),
    [redirectToActivityList, handleDuplicate],
  );

  const languageSegmentations = useMemo(() => getLanguageSegmentationGroupItemsFromApp(), []);

  const availableLanguages = getLanguagesAvailableWithCode();
  const campaignAvailableLanguage = useMemo(() => {
    return completedLanguages
      .map((lang) => availableLanguages.find((l) => l.languageCode === lang))
      .filter(Boolean) as Language[];
  }, [completedLanguages, availableLanguages]);

  const { dialog: randomDialog, askForConfirmation: randomAskForConfirmation } =
    useRandomCampaignActivationConfirmation();
  const { dialog: manualDialog, askForConfirmation: manualAskForConfirmation } =
    useManualCampaignActivationConfirmation();

  const isActive = useMemo(() => ['live', 'upcoming', 'finished'].includes(campaignStatus), [campaignStatus]);

  const handleWarningBeforeUnload = useCallback(
    (context: keyof typeof actionMapper) => {
      if (!isFormDirty) {
        return actionMapper[context]();
      }

      setWarningModalContext(context);
      setDisplayUnsavedWarning(true);
    },
    [isFormDirty, actionMapper],
  );

  const options: OptionType[] = useMemo(
    () => [
      {
        title: t('activities:actions.export_content'),
        callback: handleExport,
        trackingAction: 'export',
        trackingContext: 'content-activity',
      },
      ...(!isArchived && campaignId && !hasPendingTranslations
        ? [
            {
              title: t('activities:actions.translate'),
              callback: () => handleWarningBeforeUnload('translate'),
            },
          ]
        : []),
      {
        title: t('activities:actions.duplicate'),
        callback: () => handleWarningBeforeUnload('duplicate'),
      },
      {
        title: isArchived ? t('activities:actions.unarchive') : t('activities:actions.archive'),
        callback: () => handleArchiveClick(campaignId, isArchived),
      },
    ],
    [
      isArchived,
      campaignId,
      hasPendingTranslations,
      handleWarningBeforeUnload,
      openTranslationModal,
      handleArchiveClick,
    ],
  );

  const languageOptions = useMemo(() => {
    return [
      ...getLanguageOptions(campaignId, campaignLanguages, selectedLanguage, completedLanguages, onLanguageChange),
      {
        type: 'divider',
        key: 'divider-0',
        props: {
          style: {
            height: 0,
          },
        },
      },
      {
        type: 'standard',
        key: 'text-change',
        text: t('activities:actions.manage_languages'),
        onClick: () => setLanguagePanelVisible(true),
      },
    ] as const;
  }, [campaignId, campaignLanguages, selectedLanguage, completedLanguages, onLanguageChange]);

  const canToggleLock = campaignStatus === 'live' || campaignStatus === 'upcoming' || campaignStatus === 'finished';
  const activeToggleLabel = canToggleLock ? t('activities:status.live') : t('activities:status.active');

  const { status, text: statusText, tooltip } = getActionBarStatus(campaignStatus, campaignContentStatus);
  const tooltipHelper = status === 'important' && tooltip ? ({ icon: 'info', content: tooltip } as const) : undefined;

  const handleSave = useCallback(async () => {
    if (isActive && Object.keys(missingFields).length) {
      const errorMessage = generateErrorMessage(missingFields);

      enqueueBasicAlert(
        {
          id: 'campaign_edition_failed',
          priority: 'medium',
          status: 'error',
          title: t('campaign:edition.update_active_fail'),
        },
        ALERT_DURATION,
        errorMessage,
      );

      return;
    }

    onSave();
  }, [onSave, missingFields, generateErrorMessage, enqueueBasicAlert, isActive]);

  const onAskTranslation = useCallback(
    async (ids: number[]) => {
      const languageLabels = ids
        .map((id) => languageSegmentations.find((lang) => lang.id === id)?.label)
        .filter(Boolean) as string[];

      const languageCodes = languageLabels
        .map((label) => availableLanguages.find((lang) => lang.languageName === label)?.languageCode)
        .filter(Boolean) as LanguageCode[];

      const campaignContents = watch('contents') || watch('orderedSessions')?.flat() || [];

      const campaignContentIds = campaignContents.reduce<number[]>((acc, content) => {
        if (
          content?.statuses &&
          Object.keys(content.statuses).every(
            (lang) =>
              !languageCodes.includes(lang as LanguageCode) || content.statuses[lang as LanguageCode] === 'draft',
          )
        ) {
          acc.push(content.contentId);
        }
        return acc;
      }, []);

      displayTranslationModal(false);

      try {
        const { unwrap } = dispatch(
          requestCampaignContentsTranslation({
            contentIds: campaignContentIds,
            languageCodes,
          }),
        );

        await unwrap();

        enqueueBasicAlert(
          {
            id: 'campaign_ask_translation_success',
            priority: 'medium',
            status: 'success',
            title: t('campaign:ask_translation.success'),
          },
          ALERT_DURATION,
        );
      } catch (e) {
        enqueueBasicAlert(
          {
            id: 'campaign_ask_translation_failed',
            priority: 'medium',
            status: 'error',
            title: t('campaign:ask_translation.failed'),
          },
          ALERT_DURATION,
        );
      }
    },
    [
      dispatch,
      enqueueBasicAlert,
      displayTranslationModal,
      campaignLanguages,
      watch('contents'),
      watch('orderedSessions'),
      setValue,
    ],
  );

  const handleLiveWithoutDialogs = useCallback(async () => {
    if (!isActive && Object.keys(missingFields).length) {
      const errorMessage = generateErrorMessage(missingFields);

      enqueueBasicAlert(
        {
          id: 'campaign_edition_failed',
          priority: 'medium',
          status: 'error',
          title: t('campaign:edition.update_active_fail'),
        },
        ALERT_DURATION,
        errorMessage,
      );

      return;
    }

    onLive();
  }, [onLive, missingFields, generateErrorMessage, enqueueBasicAlert]);

  const handleLiveWithDialogs = useCallback(() => {
    if (isActive) {
      handleLiveWithoutDialogs();
      return;
    }

    if (watch('mode') === 'random') {
      randomAskForConfirmation(() => handleLiveWithoutDialogs());
      return;
    }

    manualAskForConfirmation(() => handleLiveWithoutDialogs());
  }, [randomAskForConfirmation, manualAskForConfirmation, handleLiveWithoutDialogs, watch('mode')]);

  const supportUrl = getCurrentBOLanguageOrDefault().languageCode === 'fr-FR' ? supportUrlFR : supportUrlEN;

  return (
    <>
      <SActionBar
        id={campaignId}
        tabs={tabs}
        title={campaignName}
        isActive={isActive}
        selectedTab={currentTabIndex}
        onChangeTab={onTabChange}
        onSave={handleSave}
        onBack={() => handleWarningBeforeUnload('back')}
        onToggleChange={handleLiveWithDialogs}
        options={options}
        status={status}
        statusWording={statusText}
        inactiveToggleLabel={t('activities:status.inactive')}
        canActivate={!isDirty}
        tooltipText={tooltip}
        helper={tooltipHelper}
        canLock={canToggleLock}
        activeToggleLabel={activeToggleLabel}
        locked={locked}
        onLock={onLockChange}
        lastSaved={campaignLastSave}
        optionsLanguages={languageOptions}
        canSave={!disableSave}
        isSaveLoading={saving}
        style={styles.container}
      />
      <LanguagePanel
        visible={isLanguagePanelVisible}
        missingMandatoryFields={missingFields}
        isCampaignLive={isActive}
        onClose={() => setLanguagePanelVisible(false)}
      />
      {isContentSelectionLocked && (
        <SCallout
          type="standard-dark"
          title={t('activities:warnings.random_campaign_modifications_blocked')}
          description={t('activities:warnings.random_campaign_modifications_blocked_explanation')}
          banner={{
            illustration: 'campaign',
            illustrationColor: 'WARNING',
          }}
          action={{
            label: t('activities:actions.know_more'),
            onAction: () => window.open(supportUrl, '_blank'),
            buttonType: 'button',
            rightIcon: 'external-link',
          }}
        />
      )}
      {/* Translation modal */}
      <MTranslateModal
        visible={visibleTranslationModal}
        onCancel={() => displayTranslationModal(false)}
        onAction={onAskTranslation}
        isActionLoading={false}
        languageSeg={languageSegmentations}
        alreadySelectedLanguages={campaignAvailableLanguage}
        title={t('campaign:ask_translation.title')}
        labelActionButton={t('campaign:ask_translation.label_action')}
        labelSecondButton={t('campaign:ask_translation.label_cancel')}
        explanationTitle={t('campaign:ask_translation.explanation_title')}
        explanation={t('campaign:ask_translation.explanation')}
        filterLabel={t('campaign:ask_translation.filter_label')}
      />

      {/* Archive warning modal */}
      {archiveWarningModal}

      {/* Unsaved changes warning modal */}
      <MModal
        onCloseModal={() => setDisplayUnsavedWarning(false)}
        visible={displayUnsavedWarning}
        type="dialog"
        onSecondAction={() => setDisplayUnsavedWarning(false)}
        showExitCross
        actionButtonType="destructive"
        title={t('campaign:edition.before_unload_warning.title')}
        description={t('campaign:edition.before_unload_warning.description')}
        labelActionButton={t('campaign:edition.before_unload_warning.action_button')}
        labelSecondButton={t('campaign:edition.before_unload_warning.second_button')}
        onAction={() => {
          setDisplayUnsavedWarning(false);
          actionMapper[warningModalContext]();
        }}
      />
      {randomDialog}
      {manualDialog}
    </>
  );
};
